import { OptionType } from "@portal/BaseComponents/CheckBox/type";
import { MultiSelectElement } from "@portal/BaseComponents/Filters/Elements/MultiSelect/MultiSelectElement";
import { FilterDropdownDown } from "@portal/BaseComponents/Filters/FilterDropdown/FilterDropdownDown";
import useOpen from "@portal/hooks/useOpen";
import { useDocumentTypesQuery } from "@portal/queries/document-query";
import { documentFiltersAtom } from "@portal/store/Filters/documents/document-filter";
import { resetPage, tableAtom } from "@portal/store/table/table";
import { DocumentTypes } from "@portal/types/entities/documents";
import { stringConverterPredicate } from "@portal/util/predicates";
import produce from "immer";
import { useAtom, useSetAtom } from "jotai";
import { useMemo } from "react";

const documentLabelPredicate = (option: OptionType) => option.label;
const documentOptionsPredicate = (type: DocumentTypes): OptionType => {
  return { label: type.name, value: type.id };
};

function DocumentTypesFilter() {
  const { onClose, open, toggleOpen, handleOpenChange } = useOpen({
    open: false,
  });
  const { data, isLoading } = useDocumentTypesQuery({
    entity_types: ["CUSTOMER", "INVOICE"],
  });
  const setTable = useSetAtom(tableAtom);
  const [documentFilters, setDocumentFilters] = useAtom(documentFiltersAtom);

  function handleHideReset() {
    return !documentFilters.filterValues.types?.length;
  }
  function getDocumentTypesOptions(): OptionType[] {
    return data ? data.list.map(documentOptionsPredicate) : [];
  }
  function handleReset() {
    onClose();
    setDocumentFilters((filter) => {
      return produce(filter, (draft) => {
        draft.filterValues.types = undefined;
        return draft;
      });
    });
    setTable(resetPage);
  }

  function handleSubmit(value: OptionType["value"][]) {
    setDocumentFilters((filter) => {
      return produce(filter, (draft) => {
        draft.filterValues.types = value.map(stringConverterPredicate);
        return draft;
      });
    });
    onClose();
  }
  const options = useMemo(getDocumentTypesOptions, [data]);

  const Content = (
    <MultiSelectElement
      multiSelectProps={{
        initialValue: documentFilters.filterValues.types,
        options,
        onSubmit: handleSubmit,
        onCancel: onClose,
        loading: isLoading,
      }}
      search={{
        type: "local",
        placeholder: "Search Document Types",
      }}
    />
  );

  function getSelectedValues() {
    const optionFilterPredicate = (option: OptionType) =>
      documentFilters.filterValues.types?.includes(String(option.value));
    const selectedValues = options
      .filter(optionFilterPredicate)
      .map(documentLabelPredicate);
    return selectedValues.length
      ? selectedValues.length === options.length
        ? "All"
        : selectedValues.toString()
      : "All";
  }

  const selectedFilterText = useMemo(getSelectedValues, [
    documentFilters.filterValues.types,
    options,
  ]);

  const hideReset = handleHideReset();
  return (
    <FilterDropdownDown
      filterBlockProps={{
        isDefault: true,
        toggleOpen,
        open,
        title: "Document Types",
        filterValues: selectedFilterText,
        onReset: handleReset,
        hideReset,
      }}
      filterComponent={Content}
      filterTitle={"Document Types"}
      onopenChange={handleOpenChange}
    />
  );
}

export { DocumentTypesFilter };
